import React, { useState, useEffect } from "react";
import Modal from "components/modal";
import Image from "components/image";
import NewList from "../new-list";
import WishlistToast from "../toast";
import { ReactComponent as Close } from "assets/svgs/close.svg";
import { useWishlist } from "hooks/useStore";
import { SITE_URL } from "utils/constants";
import { toast } from "react-toastify";

const WishlistModal = ({ product, isOpen, toggle }) => {
	const { savedLists, updateLists, createLists } = useWishlist(["savedLists", "updateLists", "createLists"]);
	const filteredVariantImages =
		product.variant?.images?.length > 0 ? product.variant?.images.filter(({ _type }) => _type !== "cylindo") : [];

	const [selectedListsIds, setSelectedListsIds] = useState([]);
	const [initialSelectedLists, setInitialSelectedLists] = useState([]);
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	const [newLists, setNewLists] = useState([]);
	const [updateList, setUpdateList] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (Object.values(savedLists).length === 0) {
			handleCreateNewList();
		}
		const updatedInitialSelectedLists = Object.values(savedLists)
			.filter(list => list.listcontents?.some(item => item?.epi === +product?.variant?.id))
			.map(list => list.lid);

		setInitialSelectedLists(updatedInitialSelectedLists);

		const updatedSelectedLists = [...selectedListsIds];
		updatedInitialSelectedLists.forEach(listId => {
			if (!updatedSelectedLists.includes(listId)) {
				updatedSelectedLists.push(listId);
			}
		});

		setSelectedListsIds(updatedSelectedLists);
	}, [savedLists]);

	useEffect(() => {
		const hasUpdate = !initialSelectedLists.some(list => !selectedListsIds.includes(list));
		setUpdateList(!hasUpdate);
		const anyNewListChecked = newLists?.some(list => list.checked);
		const error = newLists.some(list => list.checked && list.error);
		setIsButtonDisabled((listsToAdd.length === 0 && listsToRemove.length === 0 && !anyNewListChecked) || error);
	}, [selectedListsIds, initialSelectedLists, newLists]);

	let listsToAdd = selectedListsIds.filter(id => !initialSelectedLists.includes(id));
	const listsToRemove = initialSelectedLists.filter(list => !selectedListsIds.includes(list));

	const handleSaveToWishlist = async () => {
		setIsLoading(true);
		try {
			const productUrl = `${SITE_URL}/products/${product.handle}`;
			const newListsToAdd = newLists.filter(list => list.checked);
			if (newListsToAdd.length !== 0) {
				const lists = await createLists(newListsToAdd);
				if (lists.length !== 0) {
					listsToAdd.push(...lists?.map((list) => list.lid));
					listsToAdd = [...new Set(listsToAdd)];
					setNewLists([]);
				}
			}

			const res = await updateLists(listsToAdd, listsToRemove, product.id, product?.variant.id, productUrl);
			if (res.errors) {
				toast.error("Product not added to wishlist");
			} else {
				toast.success(
					<WishlistToast image={filteredVariantImages?.[0]}
						defaultImage={product.previewImageUrl}
						listsToAdd={listsToAdd?.length}
						listsToRemove={listsToRemove?.length} />
				);
				toggle();
			}
		} catch (error) {
			toast.error(error.message);
		}
		setIsLoading(false);
	};

	const handleSelectList = (listId) => {
		setSelectedListsIds((prevSelectedList) => {
			const isListSelected = prevSelectedList.includes(listId);
			return isListSelected
				? prevSelectedList.filter((id) => id !== listId)
				: [...prevSelectedList, listId];
		});
	};

	const handleCreateNewList = () => {
		setNewLists((prev) => [...prev, { id: crypto.randomUUID(), name: "My Wishlist" }]);
	};

	const handleUpdate = (listId, newName, newCheckedState, error) => {
		setNewLists((prevLists) =>
			prevLists.map((list) =>
				list.id === listId ? { ...list, name: newName, checked: newCheckedState, error } : list
			)
		);
	};


	return (
		<Modal
			isOpen={isOpen}
			close={toggle}
			hideHeader
			label="Add to Wishlist"
			className="text-center !w-full md:!max-w-[606px]"
		>
			<div className="p-6 w-full">
				<div className="flex gap-4 pb-4 border-b">
					<div className="size-24">
						{filteredVariantImages?.[0] ?
							<Image image={filteredVariantImages?.[0]} className="size-24" />
							: <img src={product?.previewImageUrl} className="size-24" loading="lazy" />
						}
					</div>
					<div className="flex flex-col items-start">
						<h4 className="font-semibold">{product.title}</h4>
						<p className="text-secondary text-left">{product?.variant?.title}</p>
					</div>
					<button className="p-1 ml-auto mb-auto" onClick={toggle}>
						<Close className="size-5" aria-label="Close" />
					</button>
				</div>
				<div className="flex flex-col items-start w-full">
					<p className="text-secondary py-3">Add to list</p>
					<div className="flex flex-col items-start gap-4 w-full">
						{Object.values(savedLists)?.map((list, index) => {
							const isSelected = selectedListsIds?.includes(list.lid);
							return (
								<div key={index} className="w-full flex justify-between items-center"
									tabIndex="0" aria-label={`Wishlist name ${list.lname}`} >
									<div className="flex gap-6 items-center">
										<img src={list.listcontents?.[0]?.iu || product?.previewImageUrl}
											className="size-16" loading="lazy" />
										<h5>{list.lname}</h5>
									</div>
									<input
										type="checkbox"
										onChange={() => handleSelectList(list.lid)}
										onKeyDown={(e) => e.key === "Enter" && handleSelectList(list.lid)}
										checked={isSelected}
										className="cursor-pointer"
									/>
								</div>
							);
						})}
						{newLists.map((list) => (
							<NewList
								key={list.id}
								savedLists={savedLists}
								defaultImage={product?.previewImageUrl}
								image={product.varaint?.images?.[0]}
								name={list.name}
								listId={list.id}
								newLists={newLists}
								onUpdate={handleUpdate}
								checked={list.checked}
							/>
						))
						}
					</div>
				</div>
				<div className="flex flex-col-reverse md:flex-row pt-8 gap-3">
					<button onClick={handleCreateNewList} className="w-full py-5 btn-tertiary-black" >Create New List</button>
					<button onClick={handleSaveToWishlist}
						className="w-full py-5 btn-primary"
						disabled={isButtonDisabled || isLoading}>
						{isLoading ? "Loading..." : (updateList ? "Update Lists" : "Add to List")}
					</button>

				</div>
			</div>
		</Modal>
	);
};

export default WishlistModal;